import type { ReactElement, ReactNode } from 'react';
import { Dialog as AriaDialog, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { overlayBodyClass } from './overlay';

export interface BottomSheetProps {
  title: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Actions, stacked full width with the primary one first. */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * A dialog docked to the bottom edge on phones: full width, rounded top, a
 * grab handle, and padding for the home indicator. `Dialog` renders through
 * this below `md`, so most screens never use it directly.
 */
export function BottomSheet({ title, isOpen, onOpenChange, footer, children }: BottomSheetProps): ReactElement {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      className="fixed inset-0 z-dialog m-0 p-0 border-0 bg-scrim flex flex-col justify-end"
    >
      {/* The handle, title and actions stay put; only the body scrolls, so the actions never leave the thumb's reach.
          85% of the full-screen overlay, the same height as 85dvh on a phone, and it also holds inside a gallery frame. */}
      <Modal className="w-full max-h-[85%] flex flex-col m-0 p-0 border-0 bg-surface text-ink font-ui rounded-t-panel shadow-dialog overflow-hidden">
        <AriaDialog className="m-0 px-4 pt-2 pb-[max(16px,env(safe-area-inset-bottom))] border-0 bg-surface outline-none flex flex-col gap-4 min-h-0 font-ui">
          <span aria-hidden="true" className="block self-center w-[36px] h-1 m-0 p-0 rounded-pill bg-line-strong" />
          <Heading slot="title" className="m-0 p-0 text-lg font-semibold font-ui text-ink">
            {title}
          </Heading>
          <div className={overlayBodyClass}>{children}</div>
          {footer ? <div className="m-0 p-0 flex flex-col-reverse gap-2 [&>*]:w-full">{footer}</div> : null}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}
